import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"
import { GetTicketsByFiltersDtoProps } from "api/DTOs/GetTicketsByFiltersDto"
import {
  TicketParamsProps,
  deleteTicket,
  getTicketsByFilter,
} from "api/Ticket.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import notification from "common/utils/notification"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import ModalConciliacionMovimientos from "features/Tesoreria/pages/Bancos/pages/BancosDetail/components/ModalConciliadosMovimientos/ModalConciliadosMovimientos"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useIntersectionObserver } from "hooks/useIntersectionObserver"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import TicketsTableAdapter from "../constants/TicketsTable.adapter"
import { ConciliableLabel } from "../constants/constant"
import { TICKETS_COLUMNS } from "../constants/ticketsColumns"
import { FilterMenu } from "./FilterMenu"
import MobileCamera from "./MobileCamera"
import MobileListTickets from "./MobileListTickets/MobileListTickets"
import { ModalConfirmDelete } from "./ModalConfirmDelete"
import ModalDetailTicket from "./ModalDetailTIcket"

export const Tickets = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 1023px)").matches
  )
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)

  const dtRef = useRef<DataTablePrimeReact<
    GetTicketsByFiltersDtoProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }
  const MOBILE_ITEMS_PER_PAGE = 10

  const [showModalDetail, setShowModalDetail] = useState<{
    show: boolean
    ticketId: string | null
    delete: boolean
  }>({
    show: false,
    ticketId: null,
    delete: false,
  })
  const onRowClick = async ({
    data,
  }: {
    data: GetTicketsByFiltersDtoProps
  }) => {
    if (!data.id) return
    setShowModalDetail({
      ...showModalDetail,
      show: true,
      ticketId: data.id,
    })
  }
  const fetchTickets = useCallback(
    () => (queryParams: TicketParamsProps) =>
      getTicketsByFilter({
        ...queryParams,
        SkipCount: queryParams.SkipCount ?? pagination.skipCount,
        MaxResultCount: queryParams.MaxResultCount ?? MOBILE_ITEMS_PER_PAGE,
        SortingCriteria: queryParams.SortingCriteria ?? "FechaEmision DESC",
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const {
    items,
    isLoading,
    totalCount,
    serviceQuery,
    setServiceQuery,
    fetchTableData,
  } = useFilterMenu({
    serviceCall: fetchTickets,
    serviceAdapter: TicketsTableAdapter,
  })

  const [showCamera, setShowCamera] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["tickets", serviceQuery, MOBILE_ITEMS_PER_PAGE],
      queryFn: ({ pageParam }) => {
        return getTicketsByFilter({
          ...serviceQuery,
          SkipCount: pageParam,
          MaxResultCount: MOBILE_ITEMS_PER_PAGE,
          SortingCriteria: serviceQuery.SortingCriteria ?? "FechaEmision DESC",
        })
      },
      getNextPageParam: (lastPage, allPages) => {
        const hasMore =
          typeof lastPage?.currentPage === "number" &&
          lastPage.currentPage * MOBILE_ITEMS_PER_PAGE < totalCount
        return hasMore ? allPages.length * MOBILE_ITEMS_PER_PAGE : undefined
      },
      enabled: isMobile,
      initialPageParam: 0,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    })

  useIntersectionObserver({
    target: loadMoreRef,
    onIntersect: fetchNextPage,
    enabled: hasNextPage,
  })

  const handleDeleteConfirmation = async () => {
    try {
      if (showModalDetail.ticketId) {
        await deleteTicket(showModalDetail.ticketId).then(() => {
          notification.success()
        })
      }
      handleResetPagination()
      setShowModalDetail({
        show: false,
        ticketId: null,
        delete: false,
      })
      fetchTableData()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      setShowModalDetail({
        ...showModalDetail,
        delete: false,
      })
    }
  }
  const [showModalConciliacion, setShowModalConciliacion] = useState<{
    show: boolean
    data: string | null
  }>({
    show: false,
    data: null,
  })

  const onRenderEstadoConciliacion = ({
    conciliacionBancaria,
  }: GetTicketsByFiltersDtoProps) => {
    return ConciliableLabel(
      t,
      conciliacionBancaria?.id ?? null,
      setShowModalConciliacion
    )
  }
  const onHideModalConciliacion = () => {
    setShowModalConciliacion({
      show: false,
      data: showModalConciliacion.data,
    })
  }

  const handleOnclickMobile = (ticketId: string) => {
    setShowModalDetail({
      ...showModalDetail,
      show: true,
      ticketId,
    })
  }
  const handleRedirect = (img: string) => {
    if (img) {
      history.push(ROUTES.CREAR_TICKET, { mobileCamera_b64: img })
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.matchMedia("(max-width: 1023px)").matches)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  return (
    <>
      <FilterMenu
        serviceQuery={setServiceQuery}
        sortingCriteria={sortingCriteria}
        skipCount={pagination.skipCount}
        maxResultCount={pagination.itemsPerPage}
        resetPagination={handleResetPagination}
        totalCount={totalCount}
      />
      <div className='lg:tw-hidden'>
        {showCamera ? (
          <MobileCamera
            onClose={() => setShowCamera(false)}
            onConfirm={handleRedirect}
          />
        ) : (
          <>
            <MobileListTickets
              data={data}
              isFetchingNextPage={isFetchingNextPage}
              loadMoreRef={loadMoreRef}
              onClick={handleOnclickMobile}
            />
            <button
              type='button'
              className='tw-fixed tw-bottom-6 tw-right-6 tw-bg-white tw-shadow-lg tw-rounded-full tw-w-16 tw-h-16 tw-flex tw-items-center tw-justify-center tw-transition-all'
              onClick={() => {
                setShowCamera(true)
              }}
            >
              <FontAwesomeIcon
                className='tw-w-6 tw-h-6 tw-font-black'
                icon={["fal", "plus"]}
              />
            </button>
          </>
        )}
      </div>
      <div className='tw-hidden lg:tw-block'>
        <TableERP
          refTable={dtRef}
          columns={TICKETS_COLUMNS(t, onRenderEstadoConciliacion)}
          className='!tw-z-0 tw-flex tw-flex-col tw-h-[calc(100dvh-210px)] '
          columnResizeMode='expand'
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          removableSort
          value={items}
          showGridlines
          lazy
          dataKey='id'
          scrollable
          size='small'
          onRowClick={onRowClick}
          loading={isLoading}
          emptyMessage={
            <div className='tw-text-center'>{t("ocr.emptyMsg")}</div>
          }
        />
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={totalCount}
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => {
            dtRef.current && dtRef.current?.resetScroll()
          }}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>
      <ModalDetailTicket
        showModal={!!showModalDetail.ticketId && showModalDetail.show}
        onHide={() =>
          setShowModalDetail({
            ...showModalDetail,
            show: false,
            ticketId: null,
          })
        }
        data={showModalDetail.ticketId}
        onDelete={() =>
          setShowModalDetail({ ...showModalDetail, delete: true })
        }
      />
      <ModalConfirmDelete
        isOpen={showModalDetail.delete}
        onCancel={() =>
          setShowModalDetail({ ...showModalDetail, delete: false })
        }
        onConfirm={handleDeleteConfirmation}
      />
      <ModalConciliacionMovimientos
        showModal={showModalConciliacion.show}
        conciliacionBancariaId={showModalConciliacion.data}
        onHideModalConciliacion={onHideModalConciliacion}
        fetchTable={fetchTableData}
      />
    </>
  )
}
